import { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@maestra/core/store/store';
import { artistsActions } from '@maestra/core/store/slices/artists';
import { ordemDaCota } from '@maestra/core/nucleo/cotaDePerfis';

// QUAIS PERFIS LIBERAR (QA de pagamentos, C5; decisões do Lucas, 05/10/2026), sem tela.
//
// A pessoa NÃO reveza o PRO entre os perfis que tem: só libera outro contratando mais perfis, e
// escolhe qual. Esta é a escolha, na tela: os candidatos (os perfis travados), quantos cabem, e o
// que fazer com os escolhidos (`aoConfirmar`, que manda o ajuste do plano com a lista para o
// servidor gravar). Nada aqui grava a escolha sozinho: não existe caminho do cliente para trocar
// perfil ativo.

const SEM_PERFIS: never[] = [];

export interface PedidoDeEscolha {
  /** Os perfis que podem ser escolhidos (os travados). */
  candidatos: string[];
  /** Quantos escolher. */
  limite: number;
  /** O perfil que a pessoa veio liberar (vem marcado primeiro). */
  primeiro?: string | null;
  titulo: string;
  texto: string;
  acao: string;
  /** O que fazer com os escolhidos, na ordem da tela. */
  aoConfirmar: (ids: string[]) => Promise<void>;
}

export const useEscolhaDosPerfis = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const artists = useAppSelector((s) => s.artists?.items ?? SEM_PERFIS);
  const carregando = useAppSelector((s) => !!s.artists?.loading);

  // A lista de perfis não é guardada entre recargas: quem abre o ajuste direto (ou recarrega a
  // página) chega sem ela, e a escolha abriria vazia.
  useEffect(() => {
    if (user?.id && !artists.length && !carregando) void dispatch(artistsActions.fetchArtists(user.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Os perfis da conta que disputam a cota (fora os de equipe e os legados), na ordem do banco.
  const daConta = ordemDaCota(artists.filter((a) => a.role !== 'member' && (!user?.id || a.user_id === user.id)));

  const [pedido, setPedido] = useState<PedidoDeEscolha | null>(null);
  const [marcados, setMarcados] = useState<string[]>([]);
  const [emCurso, setEmCurso] = useState(false);

  const candidatos = pedido ? daConta.filter((a) => pedido.candidatos.includes(a.id)) : [];
  const limite = pedido ? Math.max(1, Math.min(pedido.limite, candidatos.length)) : 0;

  const abrir = (p: PedidoDeEscolha) => {
    const ordem = daConta.map((a) => a.id).filter((id) => p.candidatos.includes(id));
    const base = p.primeiro && ordem.includes(p.primeiro) ? [p.primeiro, ...ordem.filter((id) => id !== p.primeiro)] : ordem;
    setMarcados(base.slice(0, Math.max(1, p.limite)));
    setPedido(p);
  };

  const alternar = (id: string) => setMarcados((m) => (
    m.includes(id) ? m.filter((x) => x !== id) : m.length < limite ? [...m, id] : m
  ));

  const confirmar = async () => {
    if (!pedido) return;
    setEmCurso(true);
    try {
      // O perfil que a pessoa veio liberar primeiro, depois os demais marcados, na ordem do banco.
      const ordem = candidatos.map((a) => a.id).filter((id) => marcados.includes(id));
      const ids = pedido.primeiro && ordem.includes(pedido.primeiro)
        ? [pedido.primeiro, ...ordem.filter((id) => id !== pedido.primeiro)]
        : ordem;
      await pedido.aoConfirmar(ids);
      setPedido(null);
    } finally {
      setEmCurso(false);
    }
  };

  return {
    /** Os perfis travados hoje (os que podem ser liberados), na ordem do banco. */
    travados: daConta.filter((a) => a.slot_state === 'suspended').map((a) => a.id),
    /** Os perfis ativos hoje (os que podem ficar numa redução), na ordem do banco. */
    ativos: daConta.filter((a) => a.slot_state !== 'suspended').map((a) => a.id),
    aberto: !!pedido,
    titulo: pedido?.titulo ?? '',
    texto: pedido?.texto ?? '',
    acao: pedido?.acao ?? '',
    limite,
    perfis: candidatos.map((a) => {
      const marcado = marcados.includes(a.id);
      return { id: a.id, nome: a.name, apoio: a.slot_state === 'suspended' ? 'Travado hoje' : 'Ativo hoje', marcado, desabilitado: !marcado && marcados.length >= limite };
    }),
    contagem: `${marcados.length} de ${limite} escolhido${limite === 1 ? '' : 's'}`,
    podeConfirmar: !!pedido && marcados.length === limite,
    emCurso,
    abrir,
    alternar,
    confirmar: () => { void confirmar(); },
    fechar: () => { if (!emCurso) setPedido(null); },
  };
};

export type EscolhaDosPerfis = ReturnType<typeof useEscolhaDosPerfis>;
